import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, CalendarPlus2, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TripResponse, tripsApi } from '../lib/api';
import { logger } from '../lib/logger';
import { getDirectionLabel, getRouteName, normalizeTime, formatDateShort } from '../lib/tripPresentation';

interface PlannedTripsPageProps {
  onGoToPlanner: () => void;
  onViewTripDetail: (trip: TripResponse) => void;
  onRequireAuth: () => void;
}

type RouteFilter = 'all' | '68' | '78';
type DirectionFilter = 'all' | 'santiago-litoral' | 'litoral-santiago';

export const PlannedTripsPage: React.FC<PlannedTripsPageProps> = ({
  onGoToPlanner,
  onViewTripDetail,
  onRequireAuth,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState<TripResponse[]>([]);
  const [routeFilter, setRouteFilter] = useState<RouteFilter>('all');
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all');

  const loadTrips = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await tripsApi.list({
        type: 'upcoming',
        sort: 'travel_date_asc',
        limit: 100,
        offset: 0,
        ...(routeFilter !== 'all' && { routeId: routeFilter }),
      });
      setTrips(response.items);
    } catch (error) {
      logger.error(
        'No se pudo cargar viajes planificados',
        error instanceof Error ? error : new Error(String(error)),
        { routeFilter }
      );
    } finally {
      setLoading(false);
    }
  }, [routeFilter, user]);

  useEffect(() => {
    void loadTrips();
  }, [loadTrips]);

  const visibleTrips = useMemo(() => {
    if (directionFilter === 'all') return trips;
    return trips.filter((trip) => trip.direction === directionFilter);
  }, [directionFilter, trips]);

  const stats = useMemo(() => {
    const route68 = trips.filter((trip) => trip.routeId === '68').length;
    const route78 = trips.filter((trip) => trip.routeId === '78').length;
    const outbound = trips.filter((trip) => trip.direction === 'santiago-litoral').length;
    const inbound = trips.filter((trip) => trip.direction === 'litoral-santiago').length;
    return { total: trips.length, route68, route78, outbound, inbound };
  }, [trips]);

  if (!user) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-white border border-gray-200 rounded-3xl p-12 text-center">
          <CalendarPlus2 className="mx-auto text-gray-400 mb-4" size={42} />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Planificados</h2>
          <p className="text-gray-500 mb-8">Inicia sesión para ver, editar y gestionar tus viajes.</p>
          <button
            onClick={onRequireAuth}
            className="px-8 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors"
          >
            Iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 pb-16 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-4xl font-black text-gray-900 mb-2">Planificados</h1>
        <p className="text-gray-500">Gestiona tus próximos viajes</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-500 mb-1">Próximos</p>
          <p className="text-4xl font-black text-blue-600">{stats.total}</p>
          <p className="text-sm text-gray-500 mt-1">viajes planificados</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-500 mb-2">Por ruta</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
              <span>Ruta 68</span>
              <span>{stats.route68}</span>
            </div>
            <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
              <span>Ruta 78</span>
              <span>{stats.route78}</span>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-500 mb-2">Por sentido</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
              <span>Stgo → Litoral</span>
              <span>{stats.outbound}</span>
            </div>
            <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
              <span>Litoral → Stgo</span>
              <span>{stats.inbound}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-6">
        <div className="overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            <button
              onClick={() => { setRouteFilter('all'); setDirectionFilter('all'); }}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                routeFilter === 'all' && directionFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setRouteFilter(routeFilter === '68' ? 'all' : '68')}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                routeFilter === '68'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ruta 68
            </button>
            <button
              onClick={() => setRouteFilter(routeFilter === '78' ? 'all' : '78')}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                routeFilter === '78'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ruta 78
            </button>
            <button
              onClick={() => setDirectionFilter(directionFilter === 'santiago-litoral' ? 'all' : 'santiago-litoral')}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                directionFilter === 'santiago-litoral'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Stgo → Litoral
            </button>
            <button
              onClick={() => setDirectionFilter(directionFilter === 'litoral-santiago' ? 'all' : 'litoral-santiago')}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                directionFilter === 'litoral-santiago'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Litoral → Stgo
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array.from({ length: 3 })].map((_, idx) => (
            <div key={idx} className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : visibleTrips.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-3xl p-12 text-center">
          <CalendarClock className="mx-auto text-gray-400 mb-4" size={42} />
          <h3 className="text-3xl font-bold text-gray-900 mb-2">No tienes viajes planificados</h3>
          <p className="text-gray-500 mb-8">Programa tu próximo viaje y aparecerá aquí.</p>
          <button
            onClick={onGoToPlanner}
            className="px-10 py-4 rounded-xl bg-blue-600 text-white font-bold text-lg hover:bg-blue-700 transition-colors"
          >
            Programar un viaje
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleTrips.map((trip) => (
            <button
              key={trip.id}
              onClick={() => onViewTripDetail(trip)}
              className="w-full text-left bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all hover:bg-blue-50 hover:border-blue-300"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold ${trip.routeId === '78' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                    R{trip.routeId}
                  </span>
                  <span className="font-semibold text-gray-900 text-base truncate">{getDirectionLabel(trip.direction)}</span>
                </div>
                <ChevronRight className="shrink-0 text-gray-400" size={18} />
              </div>
              <p className="text-gray-500 mt-1.5 flex items-center gap-2 text-sm">
                <span>{formatDateShort(trip.travelDate)}</span>
                <span>·</span>
                <span>{normalizeTime(trip.scheduledTime)}h</span>
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
